import { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Flag, AlertTriangle, Bug, ShieldAlert, HelpCircle, Send } from 'lucide-react';

interface ReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  listingTitle: string;
  listingSlug: string;
}

const TRANSLATIONS = {
  en: {
    reportTitle: 'Report this listing',
    reportDesc: 'Select a reason and provide additional details',
    inaccurateInfo: 'Inaccurate information',
    technicalIssue: 'Technical problem',
    suspiciousListing: 'Suspicious listing',
    other: 'Other',
    additionalDetails: 'Additional details (optional)',
    detailsPlaceholder: 'Please describe the issue...',
    submit: 'Submit Report',
    submitting: 'Submitting...',
    successTitle: 'Report submitted',
    successDesc: 'Thank you. Our team will review your report.',
    selectReason: 'Please select a reason',
  },
  fr: {
    reportTitle: 'Signaler cette annonce',
    reportDesc: 'Sélectionnez une raison et fournissez des détails',
    inaccurateInfo: 'Informations inexactes',
    technicalIssue: 'Problème technique',
    suspiciousListing: 'Annonce suspecte',
    other: 'Autre',
    additionalDetails: 'Détails supplémentaires (optionnel)',
    detailsPlaceholder: 'Veuillez décrire le problème...',
    submit: 'Envoyer le signalement',
    submitting: 'Envoi...',
    successTitle: 'Signalement envoyé',
    successDesc: 'Merci. Notre équipe examinera votre signalement.',
    selectReason: 'Veuillez sélectionner une raison',
  },
};

const REASONS = [
  { key: 'inaccurate', icon: AlertTriangle, color: 'border-amber-400 bg-amber-50 hover:bg-amber-100 text-amber-800' },
  { key: 'technical', icon: Bug, color: 'border-blue-400 bg-blue-50 hover:bg-blue-100 text-blue-800' },
  { key: 'suspicious', icon: ShieldAlert, color: 'border-red-400 bg-red-50 hover:bg-red-100 text-red-800' },
  { key: 'other', icon: HelpCircle, color: 'border-gray-400 bg-gray-50 hover:bg-gray-100 text-gray-800' },
] as const;

export function ReportDialog({ open, onOpenChange, listingTitle, listingSlug }: ReportDialogProps) {
  const { language } = useLanguage();
  const { toast } = useToast();
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const t = TRANSLATIONS[language as keyof typeof TRANSLATIONS] || TRANSLATIONS.en;

  const reasonLabels: Record<string, string> = {
    inaccurate: t.inaccurateInfo,
    technical: t.technicalIssue,
    suspicious: t.suspiciousListing,
    other: t.other,
  };

  const handleSubmit = async () => {
    if (!selectedReason) {
      toast({ title: t.selectReason, variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    
    // TODO: Connect to internal messaging / support system
    // For now, simulate submission
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    toast({ title: t.successTitle, description: t.successDesc });
    setSelectedReason(null);
    setDetails('');
    setSubmitting(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Flag className="h-5 w-5 text-red-500" />
            {t.reportTitle}
          </DialogTitle>
          <DialogDescription>{t.reportDesc}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Reason buttons */}
          <div className="grid grid-cols-2 gap-3">
            {REASONS.map((reason) => (
              <button
                key={reason.key}
                onClick={() => setSelectedReason(reason.key)}
                className={`flex items-center gap-2 p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                  selectedReason === reason.key
                    ? `${reason.color} ring-2 ring-offset-1 ring-current`
                    : reason.color
                }`}
              >
                <reason.icon className="h-4 w-4 flex-shrink-0" />
                <span>{reasonLabels[reason.key]}</span>
              </button>
            ))}
          </div>

          {/* Additional details */}
          <div>
            <label className="text-sm font-medium text-muted-foreground block mb-1.5">
              {t.additionalDetails}
            </label>
            <Textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder={t.detailsPlaceholder}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            {language === 'fr' ? 'Annuler' : 'Cancel'}
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || !selectedReason} className="bg-red-600 hover:bg-red-700 text-white">
            <Send className="h-4 w-4 mr-2" />
            {submitting ? t.submitting : t.submit}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
